import {
    Coordinate,
    GeoJSON,
    Geometry,
    LineString,
    Map,
    Marker,
    Polygon,
    TileLayer,
    VectorLayer,
    Extent,
} from "maptalks";
import {ToolbarControl} from 'modules/construction-work/src/Entity/Map/ToolbarControl';
import {ZoomControl} from "modules/construction-work/src/Entity/Map/ZoomControl";
import 'maptalks.control.compass/css/maptalks.control.compass.css';
import {TILE_MOYDOM} from 'ui/map/config';
import GeometryRendererFactory from "modules/construction-work/src/Entity/Map/GeometryRendererFactory";
import {ConstructionWorkMap} from "modules/construction-work/src/Entity/Work/ConstructionWorkMap";
import {IncidentMap} from "modules/construction-work/src/Entity/Work/IncidentMap";

export default class MaptalksMap {
    private config: {
        initData: {
            center: number[],
            mapConfig: {
                minZoom: number,
                maxZoom: number,
            },
        },
        repairWorkForMap: (ConstructionWorkMap | IncidentMap)[],
        event: {
            onSelectedElement: (ids: (string | number)[]) => void
            onFullScreenMap: () => void
        },
    };
    private map: Map;
    private markerLayer: VectorLayer;
    private polygonLayer: VectorLayer;
    private lineLayer: VectorLayer;
    private isFullScreenMap: boolean = false;
    private titleBtnFullScreenMap: string = 'Открыть карту';
    private svgToolbarControl = '/theme/images/map/fullScreenMap.svg';
    private toolbarControl: ToolbarControl;
    private rendererFactory: GeometryRendererFactory;
    private isMapInteractive: boolean = true;

    constructor(element, config) {
        this.config = config;
        this.polygonLayer = new VectorLayer('p');
        this.markerLayer = new VectorLayer('m');
        this.lineLayer = new VectorLayer('l');

        // TODO стоит описать структуру initData на ts
        const configCenter = config.initData.center;
        const center = new Coordinate(configCenter[1], configCenter[0]);

        const minZoom = config.initData.mapConfig.minZoom;
        const maxZoom = config.initData.mapConfig.maxZoom;

        let bounds = config.initData.mapConfig.bounds;

        if (bounds){
            bounds = new Extent(
                bounds.leftTop.x, bounds.leftTop.y,
                bounds.rightBottom.x, bounds.rightBottom.y
            );
        }

        const baseLayer = new TileLayer("tile", {
            urlTemplate: TILE_MOYDOM,
        });

        this.map = new Map(element, {
            center: center,
            zoom: 13,
            baseLayer,
            minZoom: minZoom,
            maxZoom: maxZoom,
            panAnimation: false,
            scrollWheelZoom: false,
            layers: [
                this.polygonLayer,
                this.lineLayer,
                this.markerLayer,
            ],
        });

        this.toolbarControl = new ToolbarControl({
            vertical: true,
            position: 'bottom-right',
            items: [
                {
                    item: `<img src="${this.svgToolbarControl}" alt="#"><span>${this.titleBtnFullScreenMap}</span>`,
                    click: () => this.onFullScreenMap(),
                }
            ]
        });
        this.toolbarControl.addTo(this.map);

        const zoomControl = new ZoomControl({
            position: 'bottom-left',
            zoomLevel: false
        });

        this.map.addControl(zoomControl);

        if (bounds){
            this.map.setMaxExtent(bounds);
        }

        this.handlerClickElementOnMap();

        this.rendererFactory = new GeometryRendererFactory();
    }

    private handlerClickElementOnMap(): void {
        this.map.on('click', (e) => {
            // Активируем интерактивность карты при первом клике
            if (!this.isMapInteractive) {
                this.map.config({
                    draggable: true,
                    scrollWheelZoom: true
                });
                this.isMapInteractive = true;
            }

            const point = e.containerPoint;
            const tolerance = 10;
            const geos: any[] = [];

            const allGeometries = [
                ...this.polygonLayer.getGeometries(),
                ...this.lineLayer.getGeometries(),
                ...this.markerLayer.getGeometries(),
            ];
            allGeometries.forEach((g: Geometry) => {
                if (g.containsPoint(point, tolerance)) {
                    geos.push(g);
                }
            });
            const list: (string | number)[] = [];
            let lineCoord: Coordinate | null = null;
            let markerCoord: Coordinate | null = null;
            let otherCoord: Coordinate | null = null;
            geos.forEach((g: any) => {
                if (g instanceof LineString) {
                    list.push(g.properties.id);
                    if (!lineCoord) lineCoord = g.getCenter();
                } else if (g instanceof Marker) {
                    list.push(g.properties.id);
                    markerCoord = g.getCoordinates();
                } else if (g instanceof Polygon) {
                    list.push(g.properties.id);
                    if (!otherCoord) otherCoord = g.getCenter();
                }
            });

            const finalCoord = markerCoord || lineCoord || otherCoord;
            if (finalCoord) {
                this.map.panTo(finalCoord);
            }
            if (list?.length) {
                this.config.event.onSelectedElement(list);
            }
        });
    }

    private onFullScreenMap(): void {
        this.isFullScreenMap = !this.isFullScreenMap;
        if (this.isFullScreenMap) {
            this.titleBtnFullScreenMap = 'Свернуть карту';
        } else {
            this.titleBtnFullScreenMap = 'Открыть карту';
        }
        const toolbarElement = this.toolbarControl.getContainer();
        if (toolbarElement) {
            const spanElement = toolbarElement.querySelector('span');
            if (spanElement) {
                spanElement.textContent = this.titleBtnFullScreenMap;
            }
        }
        this.config.event.onFullScreenMap();
    }

    public updatePointMap(categoryId?: string): void {
        this.polygonLayer.clear();
        this.markerLayer.clear();
        this.lineLayer.clear();

        this.config.repairWorkForMap.forEach((item: (ConstructionWorkMap | IncidentMap)) => {
            if (categoryId && item.properties.settingsMarker.id === categoryId) {
                this.render(item);
            }
            if (!categoryId) {
                this.render(item);
            }
        });
        this.map.panTo(this.map.getCenter());
    }

    public init(): void {
        this.config.repairWorkForMap.forEach((item: (ConstructionWorkMap | IncidentMap)) => {
            this.render(item);
        });
        this.map.panTo(this.map.getCenter());
    }

    private render(geoObject: (ConstructionWorkMap | IncidentMap)): void {
        GeoJSON.toGeometry(geoObject, (geometry: Geometry) => {
            const renderer = this.rendererFactory.getRenderer(geometry);
            if (renderer) {
                renderer.render(geometry, this.markerLayer, this.map);
            }
        });
    }

    public refresh(): void {
        this.map.resetCursor();
    }
}

